<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible">
            <div class="modal-background" @click="handleCancel"></div>

            <div class="modal-content">
                <div class="popup-dialog">
                    <div class="popup-title">
                        <h1 class="title is-1">{{ $t('auth.verification_code.title.' + verificationType) }}</h1>
                        <div class="popup-close" @click.prevent="handleCancel">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <div class="popup-body popup-body-bottom-border">
                        <p>{{ instructions }}</p>
                    </div>

                    <div class="popup-body popup-security-input">
                        <div class="security-visual"
                             :class="{'is-unlocking': isUnlocking, 'is-unlocked': isUnlocked}">
                            <svg class="lock" viewBox="0 0 64 64" aria-hidden="true">
                                <path class="shackle" d="M20 26v-6a12 12 0 1 1 24 0v4" fill="none"
                                      stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
                                <rect class="body" x="14" y="26" width="36" height="28" rx="6" ry="6"
                                      fill="currentColor"/>
                                <circle class="keyhole" cx="32" cy="40" r="3.5" fill="#fff"/>
                            </svg>
                            <svg class="check" viewBox="0 0 64 64" aria-hidden="true">
                                <circle class="ring" cx="32" cy="32" r="28" fill="none" stroke="currentColor"
                                        stroke-width="4"/>
                                <path class="mark" d="M18 34l9 9 19-20" fill="none" stroke="currentColor"
                                      stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>

                        <div class="code-input-group"
                             :class="{'is-shaking': isDenied, 'is-hidden': isUnlocking || isUnlocked}">
                            <input
                                ref="codeInput0"
                                v-model="codeDigit1"
                                type="text"
                                class="code-input"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                maxlength="1"
                                autocomplete="one-time-code"
                                :disabled="isLoading"
                                @input="handleInput(0, $event)"
                                @keydown="handleKeydown(0, $event)"
                                @paste="handlePaste($event)"
                                @focus="handleFocus(0)"
                            />
                            <input
                                ref="codeInput1"
                                v-model="codeDigit2"
                                type="text"
                                class="code-input"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                maxlength="1"
                                autocomplete="one-time-code"
                                :disabled="isLoading"
                                @input="handleInput(1, $event)"
                                @keydown="handleKeydown(1, $event)"
                                @paste="handlePaste($event)"
                                @focus="handleFocus(1)"
                            />
                            <input
                                ref="codeInput2"
                                v-model="codeDigit3"
                                type="text"
                                class="code-input"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                maxlength="1"
                                autocomplete="one-time-code"
                                :disabled="isLoading"
                                @input="handleInput(2, $event)"
                                @keydown="handleKeydown(2, $event)"
                                @paste="handlePaste($event)"
                                @focus="handleFocus(2)"
                            />
                            <div class="code-input-separator">-</div>
                            <input
                                ref="codeInput3"
                                v-model="codeDigit4"
                                type="text"
                                class="code-input"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                maxlength="1"
                                autocomplete="one-time-code"
                                :disabled="isLoading"
                                @input="handleInput(3, $event)"
                                @keydown="handleKeydown(3, $event)"
                                @paste="handlePaste($event)"
                                @focus="handleFocus(3)"
                            />
                            <input
                                ref="codeInput4"
                                v-model="codeDigit5"
                                type="text"
                                class="code-input"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                maxlength="1"
                                autocomplete="one-time-code"
                                :disabled="isLoading"
                                @input="handleInput(4, $event)"
                                @keydown="handleKeydown(4, $event)"
                                @paste="handlePaste($event)"
                                @focus="handleFocus(4)"
                            />
                            <input
                                ref="codeInput5"
                                v-model="codeDigit6"
                                type="text"
                                class="code-input"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                maxlength="1"
                                autocomplete="one-time-code"
                                :disabled="isLoading"
                                @input="handleInput(5, $event)"
                                @keydown="handleKeydown(5, $event)"
                                @paste="handlePaste($event)"
                                @focus="handleFocus(5)"
                            />
                        </div>

                        <p v-if="isRateLimited" class="sent-again is-rate-limited"
                           :class="{'is-hidden': isUnlocking || isUnlocked}">
                            {{ rateLimitMessage }}
                            {{ $tc('auth.verification_code.code_resend_in_seconds', resendInSeconds) }}
                        </p>
                        <p v-else-if="resendInSeconds > 0" class="sent-again"
                           :class="{'is-hidden': isUnlocking || isUnlocked}">
                            {{ $t('auth.verification_code.code_not_received') }}
                            {{ $tc('auth.verification_code.code_resend_in_seconds', resendInSeconds) }}
                        </p>
                        <p v-else class="sent-again" :class="{'is-hidden': isUnlocking || isUnlocked}">
                            {{ $t('auth.verification_code.code_not_received') }}
                            <a href="#"
                               @click.prevent="handleResend">{{ $t('auth.verification_code.code_resend_link') }}</a>
                        </p>
                    </div>

                    <div class="popup-footer">
                        <!-- Debug code display for development -->
                        <div v-if="debugCode" class="debug-code-display">
                            <div class="debug-banner"><i class="icon">bug_report</i></div>
                            <div class="debug-code"><code>{{ debugCode }}</code></div>
                        </div>

                        <a @click.prevent="handleCancel" href="#" role="button"
                           v-if="!isUnlocking && !isUnlocked">{{ $t('confirmation_dialog.deny') }}</a>
                        <g-button :loading="isLoading" :disabled="isUnlocked || isUnlocking || !isValidCodeFormat || isRateLimited"
                                  @click.prevent="handleManualSubmit">
                            {{ $t('confirmation_dialog.confirm') }}
                        </g-button>
                    </div>
                </div>
            </div>
            <!--            </g-form>-->
        </div>
    </transition>
</template>

<script lang="ts">
import Base from "../../Base.vue";

export default {
    mixins: [Base],
    props: {
        verificationType: {
            type: String,
            validator: (value) => ['email_verification', 'email_change'].includes(value)
        },
        verificationToken: {
            type: String,
            default: null
        },
        verifyEndpoint: {
            type: String,
            default: '/api/auth/verify'
        },
        resendEndpoint: {
            type: String,
            default: '/api/auth/verify/resend'
        },
        retryAfter: {
            type: Number,
            default: 60
        },
        debugCode: {
            type: String,
            default: null
        },
        context: {
            type: Object,
            default: () => ({})
        },
        instructions: String,
        expiresAt: String,
        onVerified: Function,
        onCancel: Function
    },
    data() {
        return {
            isLoading: false,
            resendInSeconds: 20,
            codeDigit1: '',
            codeDigit2: '',
            codeDigit3: '',
            codeDigit4: '',
            codeDigit5: '',
            codeDigit6: '',
            isUnlocking: false,
            isUnlocked: false,
            isDenying: false,
            isDenied: false,
            resendInterval: null,
            isRateLimited: false,
            rateLimitMessage: null,
        }
    },
    computed: {
        fullCode() {
            return this.codeDigit1 + this.codeDigit2 + this.codeDigit3 + this.codeDigit4 + this.codeDigit5 + this.codeDigit6;
        },
        isValidCodeFormat() {
            return this.fullCode.length === 6 && /^\d{6}$/.test(this.fullCode)
        },
    },
    watch: {
        codeDigit1() {
            this.onCodeChange()
        },
        codeDigit2() {
            this.onCodeChange()
        },
        codeDigit3() {
            this.onCodeChange()
        },
        codeDigit4() {
            this.onCodeChange()
        },
        codeDigit5() {
            this.onCodeChange()
        },
        codeDigit6() {
            this.onCodeChange()
        },
        fullCode(val) {
            // When 6 digits are entered, auto submit for verification
            if (val && val.length === 6 && /^\d{6}$/.test(val)) {
                if (this.isUnlocking || this.isUnlocked || this.isDenying) return;

                this.performVerification();
            } else {
                // If user edits while in invalid state, reset visuals
                if (val.length < 6 && (this.isDenying || this.isDenied)) {
                    this.resetInvalidState();
                }
            }
        }
    },
    methods: {
        onCodeChange() {
            //
        },

        async performVerification() {
            this.isLoading = true

            try {
                const response = await axios.post(this.verifyEndpoint, {
                    code: this.fullCode,
                    type: this.verificationType,
                    token: this.verificationToken,
                    context: this.context
                });

                // Success
                this.triggerUnlock();
                this.handleVerificationSuccess(response.data);
            } catch (err) {
                this.triggerDeny();

                if (err.response?.status === 429) {
                    // Rate limit error
                    this.handleRateLimitError(err.response.data);
                } else if (err.response?.status === 422) {
                    // Validation error
                    this.handleVerificationError(err.response.data);
                } else {
                    console.error(err)
                }
            } finally {
                this.isLoading = false;
            }
        },

        handleManualSubmit() {
            // Handle when user clicks the confirm button
            if (this.isValidCodeFormat) {
                this.performVerification();
            }
        },

        handleVerificationSuccess(data) {
            console.log('Verification successful:', data);

            const verificationData = {
                token: data.token,
                type: this.verificationType,
                verified: true,
                expiresAt: data.expires_at,
            };

            // Give user time to see the success animation
            setTimeout(() => {
                // Call the onVerified callback if provided
                if (typeof this.onVerified === 'function') {
                    this.onVerified(verificationData);
                }

                // Also emit for any local listeners
                this.$emit('verificationVerified', verificationData);

                this.destroy({
                    ...verificationData
                });
            }, 1200);
        },

        handleCancel() {
            // Call the onCancel callback if provided
            if (typeof this.onCancel === 'function') {
                this.onCancel();
            }

            // Destroy with cancelled flag
            this.destroy({
                verified: false,
                cancelled: true
            });
        },

        handleVerificationError(errorData) {
            // Notice with the message
            if (errorData.message) {
                this.$buefy.toast.open({
                    message: errorData.message,
                    type: 'is-danger'
                });
            }

            // Clear code on certain errors
            if (errorData.error_type === 'not_found') {
                this.clearAllDigits();
            }
        },

        handleRateLimitError(errorData) {
            const retryAfterSeconds = errorData.retry_after || 60;

            this.isRateLimited = true;
            this.rateLimitMessage = errorData.message || 'Too many attempts. Please try again later.';

            // Start countdown with the server-provided retry_after value
            this.startRateLimitCountdown(retryAfterSeconds);

            // Clear the code inputs
            this.clearAllDigits();

            // Show toast notification
            this.$buefy.toast.open({
                message: errorData.message,
                type: 'is-danger',
                duration: 5000
            });
        },

        formatTime(seconds) {
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;

            if (mins > 0) {
                return `${mins}m ${secs}s`;
            }
            return `${secs}s`;
        },

        async handleResend() {
            try {
                const response = await axios.post(this.resendEndpoint, {
                    type: this.verificationType,
                    token: this.verificationToken,
                    context: this.context
                });

                // Reset countdown
                this.startResendCountdown();
                this.clearAllDigits();

                // Update debug code if provided
                if (response.data.debug?.verification_code) {
                    this.debugCode = response.data.debug.verification_code;
                }

                // If response contains message
                if(response.data.message) {
                    this.$buefy.toast.open({
                        message: response.data.message,
                        type: 'is-success'
                    });
                }
            } catch (err) {
                if (err.response?.status === 429) {
                    // Rate limit error
                    this.handleRateLimitError(err.response.data);
                } else if(err.response?.status === 422 && err.response.data?.message) {
                    this.$buefy.toast.open({
                        message: err.response.data.message,
                        type: 'is-danger'
                    });
                } else {
                    console.warn('Error resending verification code', err);
                }
            }
        },

        triggerUnlock() {
            this.isUnlocking = true;
            setTimeout(() => {
                this.isUnlocking = false;
                this.isUnlocked = true;
            }, 400);
        },

        triggerDeny() {
            this.isDenying = true;
            this.isDenied = true;

            setTimeout(() => {
                this.isDenying = false;
            }, 450);

            setTimeout(() => {
                this.isDenied = false;

                this.clearAllDigits();
            }, 500);
        },

        resetInvalidState() {
            this.isDenying = false;
            this.isDenied = false;
        },

        clearAllDigits() {
            this.codeDigit1 = '';
            this.codeDigit2 = '';
            this.codeDigit3 = '';
            this.codeDigit4 = '';
            this.codeDigit5 = '';
            this.codeDigit6 = '';

            this.$nextTick(() => {
                const firstInput = this.$refs['codeInput0'];
                if (firstInput) {
                    firstInput.focus();
                }
            });
        },

        getCodeDigit(index) {
            const digits = [this.codeDigit1, this.codeDigit2, this.codeDigit3, this.codeDigit4, this.codeDigit5, this.codeDigit6];
            return digits[index];
        },

        setCodeDigit(index, value) {
            switch (index) {
                case 0:
                    this.codeDigit1 = value;
                    break;
                case 1:
                    this.codeDigit2 = value;
                    break;
                case 2:
                    this.codeDigit3 = value;
                    break;
                case 3:
                    this.codeDigit4 = value;
                    break;
                case 4:
                    this.codeDigit5 = value;
                    break;
                case 5:
                    this.codeDigit6 = value;
                    break;
            }
        },

        startResendCountdown() {
            // Clear any existing interval
            if (this.resendInterval) {
                clearInterval(this.resendInterval);
            }

            this.resendInSeconds = this.retryAfter;
            this.resendInterval = setInterval(() => {
                if (this.resendInSeconds > 0) {
                    this.resendInSeconds--;
                } else {
                    clearInterval(this.resendInterval);
                    this.resendInterval = null;
                }
            }, 1000);
        },

        startRateLimitCountdown(seconds) {
            // Clear any existing interval
            if (this.resendInterval) {
                clearInterval(this.resendInterval);
            }

            this.resendInSeconds = seconds;
            this.resendInterval = setInterval(() => {
                if (this.resendInSeconds > 0) {
                    this.resendInSeconds--;
                } else {
                    clearInterval(this.resendInterval);
                    this.resendInterval = null;
                    this.isRateLimited = false;
                    this.rateLimitMessage = null;
                }
            }, 1000);
        },

        handleInput(index, event) {
            const value = event.target.value;

            if(!value) {
                return;
            }

            // Only allow digits
            if (!/^\d*$/.test(value)) {
                event.target.value = '';
                return;
            }

            this.setCodeDigit(index, value);

            if (index < 5) {
                this.$nextTick(() => {
                    const nextInput = this.$refs[`codeInput${index + 1}`];
                    if (nextInput) {
                        nextInput.focus();
                        nextInput.select();
                    }
                });
            }
        },

        handleKeydown(index, event) {
            if (event.key === 'Backspace') {
                if (!this.getCodeDigit(index) && index > 0) {
                    event.preventDefault();
                    const prevInput = this.$refs[`codeInput${index - 1}`];
                    if (prevInput) {
                        prevInput.focus();
                        this.setCodeDigit(index - 1, '');
                    }
                } else if (this.getCodeDigit(index)) {
                    event.preventDefault();
                    this.setCodeDigit(index, '');
                }

                return;
            }

            if (event.key === 'ArrowLeft' && index > 0) {
                event.preventDefault();
                const prevInput = this.$refs[`codeInput${index - 1}`];
                if (prevInput) {
                    prevInput.focus();
                }

                return;
            }

            if (event.key === 'ArrowRight' && index < 5) {
                event.preventDefault();
                const nextInput = this.$refs[`codeInput${index + 1}`];
                if (nextInput) {
                    nextInput.focus();
                }

                return;
            }

            if (/^\d$/.test((event).key)) {
                event.preventDefault();
                this.setCodeDigit(index, (event).key);

                if (index < 5) {
                    this.$nextTick(() => {
                        const nextInput = this.$refs[`codeInput${index + 1}`];
                        if (nextInput) {
                            nextInput.focus();
                            nextInput.select();
                        }
                    });
                }
            }
        },

        handlePaste(event) {
            event.preventDefault();
            const pastedData = (event.clipboardData).getData('text');
            const digits = pastedData.replace(/\D/g, '').slice(0, 6);

            if (digits.length > 0) {
                for (let i = 0; i < 6; i++) {
                    this.setCodeDigit(i, (digits)[i] || '');
                }

                const lastFilledIndex = Math.min(digits.length - 1, 5);
                this.$nextTick(() => {
                    const lastInput = this.$refs[`codeInput${lastFilledIndex}`];
                    if (lastInput) {
                        lastInput.focus();
                    }
                });
            }
        },

        handleFocus(index) {
            const input = this.$refs[`codeInput${index}`];
            if (input) {
                input.select();
            }
        }
    },

    mounted() {
        this.startResendCountdown();
        this.$nextTick(() => {
            const firstInput = this.$refs['codeInput0'];
            if (firstInput) {
                firstInput.focus();
            }
        });
    },

    beforeDestroy() {
        // Clean up interval
        if (this.resendInterval) {
            clearInterval(this.resendInterval);
        }
    }
}
</script>

<style lang="scss" scoped>
.debug-code-display {
    flex: 0;
    margin-right: auto;
    gap: 0.5rem;
    display: flex;
    background: #FFF9E6;
    border: 2px solid #FFD700;
    border-radius: 0.375rem;
    padding: 0.4rem .7rem;

    .debug-banner {
        display: flex;
        align-items: center;
        color: #B8860B;
        font-size: 0.875rem;

        .icon {
            font-size: 1.125rem;
        }

        strong {
            font-weight: 600;
        }
    }

    .debug-code {

        code {
            color: #333;
            font-family: 'Courier New', monospace;
            font-weight: 700;
            padding: 0;
            letter-spacing: 0.25rem;
            background: none;
        }
    }
}

.popup-security-input {
    display: flex;
    min-height: 180px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: url('../../../../../images/layout/fingerprint-bg.svg') no-repeat #FAFAFA;
    background-size: cover;
    background-position-x: -5px;
    position: relative;
    overflow: hidden;

    a {
        color: #6A7272;
    }

    .sent-again {
        font-size: 0.875rem;
        text-align: center;

        &.is-rate-limited {
            color: #DE134E;
            font-weight: 500;
        }
    }

    .security-visual {
        position: relative;
        display: none;
        align-items: center;
        justify-content: center;
        height: 84px;
        color: #B7BDC0;

        &.is-unlocking {
            display: flex;

            .shackle {
                transform: rotate(-28deg) translateY(-2px);
            }

            .lock {
                color: #3BC173;
            }
        }

        &.is-unlocked {
            display: flex;

            .check {
                opacity: 1;
                transform: scale(1);
            }

            .lock {
                opacity: 0;
                transform: scale(.85);
            }

            .ring {
                stroke-dashoffset: 0;
            }

            .mark {
                stroke-dashoffset: 0;
            }
        }

        .ring {
            stroke-dasharray: 176;
            stroke-dashoffset: 176;
            transition: stroke-dashoffset .6s ease .1s;
        }

        .lock {
            width: 48px;
            height: 48px;
            transition: transform .4s ease, opacity .4s ease;
        }

        .shackle {
            transform-origin: 32px 20px;
            transition: transform .35s ease;
        }

        .check {
            position: absolute;
            width: 56px;
            height: 56px;
            opacity: 0;
            transform: scale(.85);
            color: #2CC36B;
            transition: transform .45s ease, opacity .45s ease;
        }

        .mark {
            stroke-dasharray: 60;
            stroke-dashoffset: 60;
            transition: stroke-dashoffset .45s ease .35s;
        }

    }
}

/* Shake animation for inputs on invalid */
@keyframes error-shake {
    0% {
        transform: translateX(0);
    }
    20% {
        transform: translateX(-6px);
    }
    40% {
        transform: translateX(6px);
    }
    60% {
        transform: translateX(-4px);
    }
    80% {
        transform: translateX(4px);
    }
    100% {
        transform: translateX(0);
    }
}

.code-input-group {
    display: flex;
    width: 100%;
    align-items: center;
    flex-direction: row;
    gap: 0.2rem;
    margin-bottom: 1rem;

    @media screen and (min-width: 769px) {
        padding-left: 6.125rem;
        padding-right: 6.125rem;
        gap: 0.3125rem;
    }

    &.is-shaking input.code-input {
        animation: error-shake .45s ease;
        border-color: #DE134E;
    }

    .code-input-separator {
        color: #DDD;
        text-align: center;
        font-size: 1.625rem;
        font-style: normal;
        font-weight: 400;
        width: 1.375rem;
        display: none;
        opacity: .65;

        @media screen and (min-width: 769px) {
            display: block;
        }
    }

    input {
        text-align: center;
        font-size: 1.3125rem;
        font-weight: 600;
        flex: 1 0 0;
        height: 3.75rem;
        width: 0;
        padding: 0.5rem 0.6125rem;
        border-radius: 0.375rem;
        border: 1px solid #DDD;
        background: #FFF;
        transition: border-color .2s ease, box-shadow .2s ease;
        box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);

        @media screen and (min-width: 769px) {
            padding: 0.5rem 0.8125rem;
        }

        &:focus {
            border-color: #6A7272;
            outline: none;
        }

    }
}
</style>
